import { useEffect, useState } from 'react'

import Uppy from '@uppy/core'
import XHRUpload from '@uppy/xhr-upload'

import { useThunkDispatch } from '@domain-group/react-hooks'

import { getAccessToken } from '@functions/accessToken'
import { baseURL } from '@functions/api'

import type { AppDispatch } from '@redux/store'
import type { EntityOptions } from '@redux/modules/module'
import * as artworkActions from '@redux/modules/artwork'

type UseUppyArtworkUploadOptions = {
  allowedFileTypes?: string,
  artwork_id: number,
  callbacks?: {
    afterAction?: () => void,
    beforeAction?: () => void,
  },
  campaign_id?: number,
  campaign_item_id?: number,
  campaign_quote_item_id?: number,
  endpoint: string,
  fieldName?: string,
  image_type?: string,
  order_item_id?: number,
  provider_id?: number,
  requestOptions: EntityOptions,
  service_product_id?: number,
}

const initializeUppy = (options: UseUppyArtworkUploadOptions, dispatch: AppDispatch) => {
  const {
    allowedFileTypes,
    artwork_id,
    callbacks,
    campaign_id,
    campaign_item_id,
    campaign_quote_item_id,
    endpoint,
    fieldName,
    order_item_id,
    requestOptions = {},
    service_product_id,
  } = options

  const { afterAction, beforeAction } = callbacks || {}

  const uppy = new Uppy({
    debug: false,
    autoProceed: true,
    onBeforeUpload: () => {
      if (beforeAction) beforeAction()
      return true
    },
    restrictions: {
      allowedFileTypes: allowedFileTypes ? allowedFileTypes.split(',') : ['application/pdf'],
    },
  })
    .use(XHRUpload, {
      endpoint: `${baseURL}/${endpoint}`,
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
      },
      formData: true,
      fieldName: fieldName || 'artwork[artwork_file]',
      getResponseError(responseText) {
        return new Error(JSON.parse(responseText).message)
      },
    })
    .on('file-added', (file) => {
      uppy.setFileMeta(file.id, {
        'artwork[id]': artwork_id || '',
        'artwork[campaign_id]': campaign_id || '',
        'artwork[campaign_item_id]': campaign_item_id || '',
        'artwork[campaign_quote_item_id]': campaign_quote_item_id || '',
        'artwork[order_item_id]': order_item_id || '',
        'artwork[service_product_id]': service_product_id || '',
        ...requestOptions,
      })
    })
    .on('upload-success', (_, response) => {
      const { body: artwork } = response
      const { hydrateArtwork } = artworkActions

      dispatch(hydrateArtwork(artwork))

      if (afterAction) afterAction()
    })

  return uppy
}

const checkRequiredOptions = (options = {}) => {
  const requiredOptions = ['endpoint']
  const optionKeys = Object.keys(options)

  return requiredOptions.every(key => optionKeys.includes(key))
}

function useUppyArtworkUpload(options = {}, deps = []) {
  const [uppy, setUppy] = useState<Uppy | undefined>(undefined)

  const dispatch = useThunkDispatch()

  useEffect(() => {
    if (checkRequiredOptions(options)){
      // @ts-expect-error TS2345
      return setUppy(initializeUppy(options, dispatch))
    }

    return () => {
      if (uppy){
        uppy.close()
        setUppy(undefined)
      }
    }
  }, [...deps])
  return uppy
}

export default useUppyArtworkUpload
